import { supabaseClient } from './supabaseClient';
import { purgeGroupProofs } from './groupDeletion';

// Member account deletion orchestration (Stage 40). Sequence:
//   1. delete_my_account() RPC — the DATA side (blocks if the user owns groups with
//      records; else anonymizes their member rows + hard-deletes empty owned groups),
//      returns the proof-blob paths to purge.
//   2. purge those blobs from Storage (best-effort; the DB is already updated).
//   3. delete-user Edge Function — deletes the caller's own auth row (service role,
//      scoped to their verified token; no body needed).
//   4. caller signs out.

// Situation data for the warnings UI. Shape:
//   { admin_groups, member_groups, blocked_by_owned_groups,
//     has_certified_record, outstanding_obligation, is_collector }
export async function getAccountDeletionPreview() {
  const { data, error } = await supabaseClient.rpc('get_account_deletion_preview');
  if (error) throw error;
  return data;
}

// Runs steps 1–3. Throws on any hard failure (the caller then keeps the user signed in
// and shows the error). On success the auth session is already invalid — the caller
// should sign out. Returns { result, purge }.
export async function runAccountDeletion() {
  // 1. Data side. This raises (and changes nothing) if the user still owns groups with
  //    records — the UI should have blocked, but the RPC enforces it regardless.
  const { data: result, error } = await supabaseClient.rpc('delete_my_account');
  if (error) throw error;

  // 2. Purge the user's own proof blobs (best-effort; failures are logged, not fatal).
  const purge = await purgeGroupProofs({ proof_paths: result?.storage_paths });

  // 3. Delete the auth row (Edge Function; supabase-js attaches the caller's bearer token).
  const { data: fnData, error: fnError } = await supabaseClient.functions.invoke('delete-user', { method: 'POST' });
  if (fnError || fnData?.error) {
    // Data is already deleted/anonymized but the auth row removal failed — surface it
    // clearly (this is a partial state; retrying the Edge Function is safe).
    throw new Error(
      `Your data was removed, but deleting your login did not complete (${fnError?.message || fnData?.error}). Please try again or contact support.`
    );
  }
  return { result, purge };
}
